import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Navigate } from 'react-router-dom';
import {
  Search, RefreshCw, Filter, X, FilterX, Tag, ChevronDown,
  Calendar, Users, Download,  ChevronRight, TrendingUp, TrendingDown
} from 'lucide-react';
import FacturacionMenuHeader from '../components/FacturacionMenuHeader';
import FactLoader from './FactLoader';
import { useQuery } from '../QueryContext';
import { useAuth } from '../AuthContext';
import { clearCache } from '../utils/dashboardCache';
import GlobalSearch from '../components/GlobalSearch';
import { calculateWorkingDays } from '../utils/timeUtils';
import './DashboardBultos.css';
import * as XLSX from 'xlsx';

/* eslint-disable react-hooks/exhaustive-deps */

// ─── Etiquetas de filtros (mismas que DailyPrincipal) ──────
const filterLabels = {
  BI_PROVEEDOR: "Casas",
  BI_MARCA: "Marca",
  BI_LINEA: "Línea",
  CLI_SEG_DES: "Segmento",
  CLI_PRIN: "Cliente",
  CLI_VEN_DES: "Vendedor",
  CLI_CANAL: "Canal",
  CLI_REG_VERD: "Región Filtro",
  V_ANULADA: "Factura Anulada",
  CLI_INACTIVO: "Cliente Inactivo",
  BI_CLASIF2: "Clasificación",
  BI_DESC1: "Producto",
  CLI_TERRITORIO: "Territorio",
  CLI_CANAL_REAL: "Canal 2",
  BI_SEGMENTO: "Portafolio",
  CLI_NOMBRE: "Cliente Detalle",
  V_FACTURA: "Factura",
  CLIENTE_ID: "Cliente ID",
  ART_ID: "SKU ID",
  BI_ASP: "ASP",
  ESPECIALISTA: "Especialista",
  USER_ESP: "Usuario Esp.",
};

const INITIAL_FILTERS = {
  BI_PROVEEDOR: [], BI_MARCA: [], BI_LINEA: [], CLI_SEG_DES: [],
  CLI_PRIN: [], CLI_VEN_DES: [], CLI_CANAL: [], CLI_REG_VERD: [],
  V_ANULADA: [], CLI_INACTIVO: [], BI_CLASIF2: [], BI_DESC1: [],
  CLI_TERRITORIO: [], CLI_CANAL_REAL: [], BI_SEGMENTO: [], CLI_NOMBRE: [],
  V_FACTURA: [], CLIENTE_ID: [], ART_ID: [], BI_ASP: [],
  ESPECIALISTA: [], USER_ESP: [],
};

const nombresMeses = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const formatNumber = (num) => {
  if (num == null) return '0';
  return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(num));
};

const getSemaforoColor = (p) => {
  const val = Number(p || 0);
  if (val < 80) return '#8b0000';
  if (val < 90) return '#ff4d4d';
  if (val < 100) return '#ffcc00';
  return '#00ba52';
};

const getOptValue = (opt) => {
  if (opt == null) return '';
  if (typeof opt === 'object' && 'value' in opt) return String(opt.value ?? '');
  return String(opt);
};
const getOptLabel = (opt) => {
  if (opt == null) return '';
  if (typeof opt === 'object' && 'label' in opt) return opt.label ?? '';
  return String(opt);
};

// ─── Filtro reutilizable ──────────────────────────────────
const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  const normalizedSelected = useMemo(
    () => (selectedValues || []).map(v => String(v)),
    [selectedValues]
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const totalOptions = options.length;
  const hasSelection = normalizedSelected.length > 0;
  const allOptionsSelected = totalOptions > 0 && normalizedSelected.length === totalOptions;

  const displayValue = allOptionsSelected
    ? 'Todos'
    : hasSelection
      ? (normalizedSelected.length === 1
          ? (() => {
              const found = options.find(opt => getOptValue(opt) === normalizedSelected[0]);
              return found != null ? getOptLabel(found) : normalizedSelected[0];
            })()
          : `${normalizedSelected.length} Selec.`)
      : 'Todos';

  const showSearch = options.length > 12;
  const filteredOptions = options
    .filter(opt => String(getOptLabel(opt)).toLowerCase().includes(searchTerm.toLowerCase()))
    .slice(0, 100);

  const areAllSelected = filteredOptions.length > 0 && filteredOptions.every(opt =>
    normalizedSelected.includes(getOptValue(opt))
  );

  const handleSelectAll = () => {
    if (areAllSelected) {
      const newSelected = (selectedValues || []).filter(val =>
        !filteredOptions.some(opt => getOptValue(opt) === String(val))
      );
      onChange(name, newSelected);
    } else {
      onChange(name, filteredOptions.map(opt => getOptValue(opt)));
    }
  };

  const handleSingleToggle = useCallback((optValue) => {
    const optStr = String(optValue);
    const exists = normalizedSelected.includes(optStr);
    const newVals = exists
      ? (selectedValues || []).filter(v => String(v) !== optStr)
      : [...(selectedValues || []), optStr];
    onChange(name, newVals);
  }, [selectedValues, normalizedSelected, onChange, name]);

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button
        className={`filter-pill-button ${hasSelection ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{displayValue}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className={`filter-dropdown-panel ${showSearch ? 'with-search' : ''}`}>
          {showSearch && (
            <div className="search-wrapper">
              <Search size={14} className="search-icon" />
              <input type="text" placeholder="Buscar..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} autoFocus />
            </div>
          )}
          {filteredOptions.length > 0 && (
            <label className="checkbox-row select-all-row">
              <input type="checkbox" checked={areAllSelected} onChange={handleSelectAll} />
              <span>Seleccionar Todos ({filteredOptions.length})</span>
            </label>
          )}
          <div className="options-list">
            {filteredOptions.map((opt, i) => {
              const optValue = getOptValue(opt);
              const optLabel = getOptLabel(opt);
              return (
                <label key={`${name}-${optValue}-${i}`} className="checkbox-row">
                  <input type="checkbox" checked={normalizedSelected.includes(optValue)}
                    onChange={() => handleSingleToggle(optValue)} />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
          {hasSelection && (
            <button className="reset-pill-link" onClick={() => onChange(name, [])}>
              Limpiar ({normalizedSelected.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// KPIFact — SOLO vista Especialista / SOLO Bultos / SIN USD
// ═══════════════════════════════════════════════════════════
const KPIFact = () => {
  const { salesQueryPML, getSalesFilters, loading, error } = useQuery();
  const { session, profile } = useAuth();

  const allowedRoles = [
    'Gerente de Administración de Ventas',
    'Coordinador de Inteligencia de Negocio',
    'Coordinadora de Administración de Ventas',
  ];
  const userRole = session?.user?.cargo;

  // ── Responsive ─────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Estado ─────────────────────────────────────────────
  const [localLoading, setLocalLoading]     = useState(true);
  const [querying, setQuerying]             = useState(false);
  const [treeData, setTreeData]             = useState([]);
  const [totals, setTotals]                 = useState({ cuota_bultos: 0, real_bultos: 0, real_bultos_prev: 0 });
  const [config, setConfig]                 = useState(null);
  const [filters, setFilters]               = useState(INITIAL_FILTERS);
  const filtersInitialized                  = useRef(false);
  const [availableOptions, setAvailableOptions] = useState({});
  const [showFilters, setShowFilters]       = useState(false);
  const [availableYears, setAvailableYears] = useState([]);

  const [selectedMeses, setSelectedMeses]   = useState([nombresMeses[new Date().getMonth()]]);
  const [selectedDay, setSelectedDay]       = useState([]);
  const currentYear                         = new Date().getFullYear();
  const [selectedAnios, setSelectedAnios]   = useState([currentYear]);

  const [comparisonMode, setComparisonMode] = useState('CUOTA');
  const [workDayMetrics, setWorkDayMetrics] = useState({ total: 0, elapsed: 0, percentage: 0 });

  // Expansibles (5 niveles)
  const [expandedEsp, setExpandedEsp]   = useState({});
  const [expandedReg, setExpandedReg]   = useState({});
  const [expandedVend, setExpandedVend] = useState({});
  const [expandedCli, setExpandedCli]   = useState({});

  const toggleEsp  = (k) => setExpandedEsp(p => ({ ...p, [k]: !p[k] }));
  const toggleReg  = (k) => setExpandedReg(p => ({ ...p, [k]: !p[k] }));
  const toggleVend = (k) => setExpandedVend(p => ({ ...p, [k]: !p[k] }));
  const toggleCli  = (k) => setExpandedCli(p => ({ ...p, [k]: !p[k] }));

  const requestRef = useRef(0);

  // ── Días hábiles ───────────────────────────────────────
  useEffect(() => {
    const metrics = calculateWorkingDays(selectedMeses, selectedAnios, nombresMeses);
    setWorkDayMetrics(metrics);
  }, [selectedMeses, selectedAnios]);

  // ── Carga de config (filtros de ventas) ────────────────
  useEffect(() => {
    const loadDashboardData = async () => {
      setLocalLoading(true);
      try {
        const conf = await getSalesFilters();
        if (!conf) throw new Error("Configuración de ventas no disponible.");

        setConfig(conf);
        setAvailableOptions(conf.availableOptions || {});
        setAvailableYears(conf.availableYears || [currentYear]);

        const allSegments = conf.availableOptions?.CLI_SEG_DES || [];
        const excludedSegments = ['DONACIONES', 'EMPLEADOS', 'NO APLICA'];
        const prefilteredSegments = allSegments.filter(segment =>
          !excludedSegments.some(ex => segment.toUpperCase().includes(ex.toUpperCase()))
        );

        setFilters({
          ...INITIAL_FILTERS,
          V_ANULADA: ['0'],
          CLI_INACTIVO: ['0'],
          CLI_SEG_DES: prefilteredSegments
        });

        const defaultYear = conf.currentYear || currentYear;
        setSelectedAnios([defaultYear]);
        setSelectedMeses([nombresMeses[new Date().getMonth()]]);
        filtersInitialized.current = true;
      } catch (err) {
        console.error('Error al cargar la configuración:', err);
      } finally {
        setLocalLoading(false);
      }
    };
    loadDashboardData();
  }, []);

  // ── Procesar respuesta (jerarquía especialista → ... → SKU) ───
  const processResponse = useCallback((response) => {
    const rows = response.proveedor_marca_linea_table || [];
    const hasThirdLevel  = rows.some(row => row.col3_name && row.col3_name.trim() !== '');
    const hasFourthLevel = rows.some(row => row.col4_name && row.col4_name.trim() !== '');

    const calcVariacion = (actual, prev) => {
      if (prev == null || isNaN(prev)) return null;
      if (prev === 0) return actual > 0 ? 100 : 0;
      return ((actual - prev) / prev) * 100;
    };

    const tree = {};
    for (let i = 0, len = rows.length; i < len; i++) {
      const row = rows[i];
      const l1 = row.col1_name || 'SIN ESPECIALISTA';
      const l2 = row.col2_name || 'SIN REGIÓN';
      const l3 = hasThirdLevel  ? (row.col3_name || 'SIN VENDEDOR') : null;
      const l4 = hasFourthLevel ? (row.col4_name || 'SIN CLIENTE') : null;
      const linea = row.BI_LINEA || 'SIN LÍNEA';
      const prevB = Number(row.real_bultos_prev ?? 0);

      if (!tree[l1]) tree[l1] = {
        name: l1, cuota_bultos: 0, real_bultos: 0, real_bultos_prev: 0, regiones: {}
      };
      const n1 = tree[l1];
      n1.cuota_bultos     += Number(row.cuota_bultos || 0);
      n1.real_bultos      += Number(row.real_bultos || 0);
      n1.real_bultos_prev += prevB;

      if (!n1.regiones[l2]) n1.regiones[l2] = {
        name: l2, cuota_bultos: 0, real_bultos: 0, real_bultos_prev: 0,
        ...(hasThirdLevel ? { vendedores: {} } : { lineas: [] })
      };
      const n2 = n1.regiones[l2];
      n2.cuota_bultos     += Number(row.cuota_bultos || 0);
      n2.real_bultos      += Number(row.real_bultos || 0);
      n2.real_bultos_prev += prevB;

      if (hasThirdLevel) {
        if (!n2.vendedores[l3]) n2.vendedores[l3] = {
          name: l3, cuota_bultos: 0, real_bultos: 0, real_bultos_prev: 0,
          ...(hasFourthLevel ? { clientes: {} } : { lineas: [] })
        };
        const n3 = n2.vendedores[l3];
        n3.cuota_bultos     += Number(row.cuota_bultos || 0);
        n3.real_bultos      += Number(row.real_bultos || 0);
        n3.real_bultos_prev += prevB;

        if (hasFourthLevel) {
          if (!n3.clientes[l4]) n3.clientes[l4] = {
            name: l4, cuota_bultos: 0, real_bultos: 0, real_bultos_prev: 0, lineas: []
          };
          const n4 = n3.clientes[l4];
          n4.cuota_bultos     += Number(row.cuota_bultos || 0);
          n4.real_bultos      += Number(row.real_bultos || 0);
          n4.real_bultos_prev += prevB;
          n4.lineas.push({ name: linea, ...row });
        } else {
          n3.lineas.push({ name: linea, ...row });
        }
      } else {
        n2.lineas.push({ name: linea, ...row });
      }
    }

    const calcAll = (arr) => {
      const sorted = arr.sort((a, b) => b.real_bultos - a.real_bultos);
      for (const item of sorted) {
        item.var_bultos = calcVariacion(item.real_bultos, item.real_bultos_prev);
        if (item.regiones) {
          item.regiones = Object.values(item.regiones).map(r => {
            r.var_bultos = calcVariacion(r.real_bultos, r.real_bultos_prev);
            if (r.vendedores) {
              r.vendedores = Object.values(r.vendedores).map(v => {
                v.var_bultos = calcVariacion(v.real_bultos, v.real_bultos_prev);
                if (v.clientes) {
                  v.clientes = Object.values(v.clientes).map(c => {
                    c.var_bultos = calcVariacion(c.real_bultos, c.real_bultos_prev);
                    c.lineas = c.lineas
                      .sort((a, b) => b.real_bultos - a.real_bultos)
                      .map(l => ({
                        ...l,
                        var_bultos: calcVariacion(l.real_bultos || 0, Number(l.real_bultos_prev ?? 0))
                      }));
                    return c;
                  }).sort((a, b) => b.real_bultos - a.real_bultos);
                } else if (v.lineas) {
                  v.lineas = v.lineas
                    .sort((a, b) => b.real_bultos - a.real_bultos)
                    .map(l => ({
                      ...l,
                      var_bultos: calcVariacion(l.real_bultos || 0, Number(l.real_bultos_prev ?? 0))
                    }));
                }
                return v;
              }).sort((a, b) => b.real_bultos - a.real_bultos);
            } else if (r.lineas) {
              r.lineas = r.lineas
                .sort((a, b) => b.real_bultos - a.real_bultos)
                .map(l => ({
                  ...l,
                  var_bultos: calcVariacion(l.real_bultos || 0, Number(l.real_bultos_prev ?? 0))
                }));
            }
            return r;
          }).sort((a, b) => b.real_bultos - a.real_bultos);
        }
      }
      return sorted;
    };

    const processedData = calcAll(Object.values(tree));
    const totalsRaw = response.proveedor_marca_linea_total_table?.[0] || {};

    return {
      treeData: processedData,
      totals: {
        cuota_bultos:     Number(totalsRaw.total_cuota_bultos || 0),
        real_bultos:      Number(totalsRaw.total_real_bultos || 0),
        real_bultos_prev: Number(totalsRaw.total_real_bultos_prev || 0),
      }
    };
  }, []);

  // ── Fetch principal ────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!filtersInitialized.current) return;
    const requestId = ++requestRef.current;
    setQuerying(true);
    try {
      const payload = {
        metric_type: 'BULTOS',
        comparison_mode: comparisonMode,
        dev_mode: 'NONE',
        page_anios: selectedAnios.filter(a => a),
        page_meses: selectedMeses.filter(m => m),
        page_dias: selectedDay.filter(d => d),
        filters,
        breakdown_type: 'ESP_REG_VEN_CLI',
        chart_year: selectedAnios?.[0] || null,
        low_compliance: false,
        user_info: profile ? { username: profile.username, cargo: profile.cargo } : null
      };

      const rawResponse = await salesQueryPML(payload);
      if (requestId !== requestRef.current) return;

      const processed = processResponse(rawResponse);
      setTreeData(processed.treeData);
      setTotals(processed.totals);
    } catch (err) {
      if (requestId === requestRef.current) console.error("❌ Error fetchData:", err);
    } finally {
      if (requestId === requestRef.current) setQuerying(false);
    }
  }, [comparisonMode, selectedMeses, selectedAnios, selectedDay, filters, salesQueryPML, profile, processResponse]);

  // Debounce
  const debouncedFetch = useRef();
  useEffect(() => {
    if (!filtersInitialized.current) return;
    debouncedFetch.current = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(debouncedFetch.current);
  }, [comparisonMode, selectedMeses, selectedAnios, selectedDay, filters]);

  // Limpiar expansibles al cambiar datos
  useEffect(() => {
    setExpandedEsp({});
    setExpandedReg({});
    setExpandedVend({});
    setExpandedCli({});
  }, [treeData]);

  // ── Refresh ────────────────────────────────────────────
  const handleRefresh = async () => {
    clearCache();
    await fetchData();
  };

  // ── Reset filtros ──────────────────────────────────────
  const handleResetFilters = useCallback(() => {
    if (!config?.availableOptions?.CLI_SEG_DES) return;
    const allSegments = config.availableOptions.CLI_SEG_DES;
    const excludedSegments = ['DONACIONES', 'EMPLEADOS', 'NO APLICA'];
    const prefilteredSegments = allSegments.filter(segment =>
      !excludedSegments.some(ex => segment.toUpperCase().includes(ex.toUpperCase()))
    );
    setFilters({
      ...INITIAL_FILTERS,
      V_ANULADA: ['0'],
      CLI_INACTIVO: ['0'],
      CLI_SEG_DES: prefilteredSegments
    });
  }, [config]);

  // ── Export Excel (solo Bultos) ─────────────────────────
  const exportToExcel = useCallback((data) => {
    const rows = [];
    const pushLinea = (esp, reg, vend, cli, lin) => {
      rows.push({
        'Especialista': esp?.name || '',
        'Región':       reg?.name || '',
        'Vendedor':     vend?.name || '',
        'Cliente':      cli?.name || '',
        'SKU ID':       lin.ART_ID || '',
        'SKU':          lin.name,
        'Cuota Bultos': Math.round(lin.cuota_bultos || 0),
        'Real Bultos':  Math.round(lin.real_bultos  || 0),
        'Cumplimiento': ((lin.cuota_bultos > 0 ? (lin.real_bultos / lin.cuota_bultos * 100) : 0)).toFixed(1) + '%'
      });
    };

    data.forEach(esp => {
      esp.regiones.forEach(reg => {
        if (reg.vendedores) {
          reg.vendedores.forEach(vend => {
            if (vend.clientes) {
              vend.clientes.forEach(cli => {
                cli.lineas.forEach(lin => pushLinea(esp, reg, vend, cli, lin));
              });
            } else {
              vend.lineas.forEach(lin => pushLinea(esp, reg, vend, null, lin));
            }
          });
        } else {
          reg.lineas.forEach(lin => pushLinea(esp, reg, null, null, lin));
        }
      });
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Especialista");
    XLSX.writeFile(wb, `KPI_Especialista_Bultos_${new Date().getTime()}.xlsx`);
  }, []);

  // ── Guards ─────────────────────────────────────────────
  if ((loading || localLoading) && !filtersInitialized.current) {
    return <FactLoader message="Cargando datos..." />;
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#ff6b6b' }}>
        <h3>Error de conexión</h3>
        <p>{error}</p>
        <button onClick={handleRefresh} className="refresh-button" style={{ marginTop: '20px' }}>
          <RefreshCw size={20} /> Reintentar
        </button>
      </div>
    );
  }

  if (session && !allowedRoles.includes(userRole)) {
    return <Navigate to="/home" replace />;
  }

  const cumplimiento = totals.cuota_bultos > 0
    ? (totals.real_bultos / totals.cuota_bultos) * 100
    : 0;
  const timeVsSalesDiff = cumplimiento - Number(workDayMetrics.percentage);

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  const GRID = '3fr 1fr 1fr 1fr 1fr 1.2fr';

  return (
    <motion.div
      className="dashboard-container"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
      transition={isMobile ? { duration: 0.3 } : { duration: 0.6, ease: "easeInOut" }}
    >
      <FacturacionMenuHeader />
      {querying && <div className="querying-overlay active" />}

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={isMobile ? { duration: 0.2 } : { duration: 0.5, ease: "easeOut" }}
      >
        <header className="header-flex">
          <div className="title-section">
            <h1 className="dashboard-title">Especialista · Bultos</h1>
            <div className="title-underline"></div>
          </div>
          <div className="date-selectors">
            <button className="refresh-button" onClick={handleRefresh} title="Actualizar" disabled={querying}>
              <RefreshCw size={16} className={querying ? "spin-anim" : ""} />
            </button>
            <button className="refresh-button" onClick={() => exportToExcel(treeData)} title="Exportar Excel" style={{ background: '#00ba52' }}>
              <Download size={16} />
            </button>
            <button className="mobile-filter-btn" onClick={() => setShowFilters(true)}>
              <Filter size={16} />
              <span>Filtros</span>
            </button>
            <div className="switch-toggle-group">
              <button className={`switch-toggle-btn ${comparisonMode === 'CUOTA' ? 'active' : ''}`} onClick={() => setComparisonMode('CUOTA')}>Cuota</button>
              <button className={`switch-toggle-btn ${comparisonMode === 'FORECAST' ? 'active' : ''}`} onClick={() => setComparisonMode('FORECAST')}>Forecast</button>
            </div>
            <div className="date-filters-group">
              <MultiSelectFilter
                label="Día" name="dias" icon={Calendar}
                options={Array.from({ length: 31 }, (_, i) => (i + 1).toString())}
                selectedValues={selectedDay}
                onChange={(_, vals) => setSelectedDay(vals)}
              />
              <MultiSelectFilter
                label="Mes" name="meses" icon={Calendar}
                options={nombresMeses}
                selectedValues={selectedMeses}
                onChange={(_, vals) => setSelectedMeses(vals)}
              />
              <MultiSelectFilter
                label="Año" name="anios" icon={Calendar}
                options={availableYears.map(y => ({ value: y, label: y.toString() }))}
                selectedValues={selectedAnios}
                onChange={(_, vals) => setSelectedAnios(vals)}
              />
            </div>
            <div style={isMobile ? { width: '100%', marginTop: '8px' } : {}}>
              <GlobalSearch
                availableOptions={availableOptions}
                filterLabels={filterLabels}
                filters={filters}
                setFilters={setFilters}
              />
            </div>
          </div>
        </header>

        {/* Working days bar */}
        <div className="working-days-bar">
          <div className="wd-item"><span>Días Hábiles:</span> <strong>{workDayMetrics.total}</strong></div>
          <div className="wd-item"><span>Transcurridos:</span> <strong>{workDayMetrics.elapsed}</strong></div>
          <div className="wd-item"><span>Avance:</span> <strong>{workDayMetrics.percentage}%</strong></div>
          <div className="wd-separator"></div>
          <div className={`wd-item variation-item ${timeVsSalesDiff >= 0 ? 'positive' : 'negative'}`}>
            {timeVsSalesDiff >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            <strong>{timeVsSalesDiff.toFixed(1)}%</strong>
            <span className="wd-label">(vs Avance)</span>
          </div>
        </div>

        {/* Sidebar filtros */}
        <div className={`filters-sidebar-container ${showFilters ? 'open' : ''}`}>
          <div className="filters-sidebar-header">
            <h3>Filtros Disponibles</h3>
            <div className="filters-header-actions">
              <button className="reset-filters-btn" onClick={handleResetFilters} title="Restablecer filtros">
                <FilterX size={16} />
              </button>
              <button className="close-filters-btn" onClick={() => setShowFilters(false)}>
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="filters-grid" style={{ gap: '8px' }}>
            {Object.keys(filters).map((key) => (
              <MultiSelectFilter
                key={key}
                label={filterLabels[key] || key}
                name={key}
                icon={key.includes('CLI') ? Users : Tag}
                options={availableOptions[key] || []}
                selectedValues={filters[key] || []}
                onChange={(name, vals) => setFilters(prev => ({ ...prev, [name]: vals }))}
              />
            ))}
            <button className="filter-pill-button reset-filters" onClick={handleResetFilters}>
              <FilterX size={12} className="pill-icon" />
              <span className="pill-label">Limpiar</span>
            </button>
          </div>
        </div>
        {showFilters && <div className="filters-overlay" onClick={() => setShowFilters(false)} />}

        {/* ══════════ TABLA ══════════ */}
        <div
          className="detail-table-container2"
          style={{
            opacity: querying ? 0.8 : 1,
            transition: 'opacity 0.3s',
            marginTop: '20px',
            height: isMobile ? 'auto' : undefined,
          }}
        >
          <div className="chart-container mini table-fixed-height" style={{ flex: 1, gridColumn: 'span 2' }}>
            <div className="table-header-container">
              <h3 className="chart-title">Especialista · Región · Vendedor · Cliente · SKU</h3>
            </div>

            {isMobile ? (
              /* ══════ MÓVIL ══════ */
              <div style={{ marginTop: '10px' }}>
                <div style={{ maxHeight: '500px', overflowY: 'auto', overflowX: 'hidden', borderRadius: '8px', border: '1px solid #334155', backgroundColor: '#0f172a' }}>
                  <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '0.65rem', color: '#e2e8f0' }}>
                    <colgroup>
                      <col style={{ width: '44%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '8%' }} />
                    </colgroup>
                    <thead>
                      <tr style={{ position: 'sticky', top: 0, backgroundColor: '#1e293b', color: '#94a3b8', textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 600, zIndex: 2 }}>
                        <th style={{ padding: '6px 4px', textAlign: 'left' }}>Nivel</th>
                        <th style={{ padding: '6px 4px', textAlign: 'right' }}>{comparisonMode === 'FORECAST' ? 'FC' : 'Cuota'}</th>
                        <th style={{ padding: '6px 4px', textAlign: 'right' }}>Real</th>
                        <th style={{ padding: '6px 4px', textAlign: 'right' }}>% Efec.</th>
                        <th style={{ padding: '6px 4px', textAlign: 'center' }}>Est.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {treeData.map((esp, eIdx) => {
                        const efecEsp = esp.cuota_bultos > 0 ? (esp.real_bultos / esp.cuota_bultos) * 100 : 0;
                        const isEspOpen = expandedEsp[esp.name];
                        return (
                          <React.Fragment key={`m-esp-${eIdx}`}>
                            <tr onClick={() => toggleEsp(esp.name)} style={{ cursor: 'pointer', backgroundColor: eIdx % 2 === 0 ? '#0f172a' : '#1e293b', borderBottom: '1px solid #334155' }}>
                              <td style={{ padding: '4px 4px', display: 'flex', alignItems: 'flex-start', gap: '3px' }}>
                                {isEspOpen ? <ChevronDown size={12} style={{ flexShrink: 0, marginTop: 2 }} /> : <ChevronRight size={12} style={{ flexShrink: 0, marginTop: 2 }} />}
                                <span style={{ wordBreak: 'break-word' }} title={esp.name}>{esp.name || '---'}</span>
                              </td>
                              <td style={{ textAlign: 'right' }}>{formatNumber(esp.cuota_bultos)}</td>
                              <td style={{ textAlign: 'right' }}>{formatNumber(esp.real_bultos)}</td>
                              <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{efecEsp.toFixed(1)}%</td>
                              <td style={{ textAlign: 'center' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: getSemaforoColor(efecEsp), display: 'inline-block' }}></div></td>
                            </tr>
                            {isEspOpen && esp.regiones.map((reg, rIdx) => {
                              const efecReg = reg.cuota_bultos > 0 ? (reg.real_bultos / reg.cuota_bultos) * 100 : 0;
                              const isRegOpen = expandedReg[`${esp.name}||${reg.name}`];
                              return (
                                <React.Fragment key={`m-reg-${eIdx}-${rIdx}`}>
                                  <tr onClick={() => toggleReg(`${esp.name}||${reg.name}`)} style={{ cursor: 'pointer', backgroundColor: '#1e293b', borderBottom: '1px solid #334155' }}>
                                    <td style={{ paddingLeft: '20px', display: 'flex', alignItems: 'flex-start', gap: '3px' }}>
                                      {isRegOpen ? <ChevronDown size={12} style={{ flexShrink: 0, marginTop: 2 }} /> : <ChevronRight size={12} style={{ flexShrink: 0, marginTop: 2 }} />}
                                      <span style={{ wordBreak: 'break-word' }} title={reg.name}>{reg.name || '---'}</span>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>{formatNumber(reg.cuota_bultos)}</td>
                                    <td style={{ textAlign: 'right' }}>{formatNumber(reg.real_bultos)}</td>
                                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{efecReg.toFixed(1)}%</td>
                                    <td style={{ textAlign: 'center' }}><div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: getSemaforoColor(efecReg), display: 'inline-block' }}></div></td>
                                  </tr>
                                  {isRegOpen && (reg.vendedores || []).map((vend, vIdx) => {
                                    const efecVend = vend.cuota_bultos > 0 ? (vend.real_bultos / vend.cuota_bultos) * 100 : 0;
                                    const isVendOpen = expandedVend[`${esp.name}||${reg.name}||${vend.name}`];
                                    return (
                                      <React.Fragment key={`m-vend-${eIdx}-${rIdx}-${vIdx}`}>
                                        <tr onClick={() => toggleVend(`${esp.name}||${reg.name}||${vend.name}`)} style={{ cursor: 'pointer', backgroundColor: '#1e293b', borderBottom: '1px solid #334155' }}>
                                          <td style={{ paddingLeft: '35px', display: 'flex', alignItems: 'flex-start', gap: '3px' }}>
                                            {isVendOpen ? <ChevronDown size={12} style={{ flexShrink: 0, marginTop: 2 }} /> : <ChevronRight size={12} style={{ flexShrink: 0, marginTop: 2 }} />}
                                            <span style={{ wordBreak: 'break-word' }} title={vend.name}>{vend.name || '---'}</span>
                                          </td>
                                          <td style={{ textAlign: 'right' }}>{formatNumber(vend.cuota_bultos)}</td>
                                          <td style={{ textAlign: 'right' }}>{formatNumber(vend.real_bultos)}</td>
                                          <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{efecVend.toFixed(1)}%</td>
                                          <td style={{ textAlign: 'center' }}><div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: getSemaforoColor(efecVend), display: 'inline-block' }}></div></td>
                                        </tr>
                                        {isVendOpen && (vend.clientes || []).map((cli, cIdx) => {
                                          const efecCli = cli.cuota_bultos > 0 ? (cli.real_bultos / cli.cuota_bultos) * 100 : 0;
                                          const isCliOpen = expandedCli[`${esp.name}||${reg.name}||${vend.name}||${cli.name}`];
                                          return (
                                            <React.Fragment key={`m-cli-${eIdx}-${rIdx}-${vIdx}-${cIdx}`}>
                                              <tr onClick={() => toggleCli(`${esp.name}||${reg.name}||${vend.name}||${cli.name}`)} style={{ cursor: 'pointer', backgroundColor: '#1e293b', borderBottom: '1px solid #334155' }}>
                                                <td style={{ paddingLeft: '50px', display: 'flex', alignItems: 'flex-start', gap: '3px' }}>
                                                  {isCliOpen ? <ChevronDown size={12} style={{ flexShrink: 0, marginTop: 2 }} /> : <ChevronRight size={12} style={{ flexShrink: 0, marginTop: 2 }} />}
                                                  <span style={{ wordBreak: 'break-word' }} title={cli.name}>{cli.name || '---'}</span>
                                                </td>
                                                <td style={{ textAlign: 'right' }}>{formatNumber(cli.cuota_bultos)}</td>
                                                <td style={{ textAlign: 'right' }}>{formatNumber(cli.real_bultos)}</td>
                                                <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{efecCli.toFixed(1)}%</td>
                                                <td style={{ textAlign: 'center' }}><div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: getSemaforoColor(efecCli), display: 'inline-block' }}></div></td>
                                              </tr>
                                              {isCliOpen && cli.lineas.map((lin, lIdx) => {
                                                const efecLin = lin.cuota_bultos > 0 ? (lin.real_bultos / lin.cuota_bultos) * 100 : 0;
                                                return (
                                                  <tr key={`m-lin-${eIdx}-${rIdx}-${vIdx}-${cIdx}-${lIdx}`} style={{ backgroundColor: '#0f172a', borderBottom: '1px solid #334155', fontSize: '0.6rem' }}>
                                                    <td style={{ paddingLeft: '65px', wordBreak: 'break-word' }} title={lin.name}>{lin.name || '---'}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatNumber(lin.cuota_bultos)}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatNumber(lin.real_bultos)}</td>
                                                    <td style={{ textAlign: 'right' }}>{efecLin.toFixed(1)}%</td>
                                                    <td style={{ textAlign: 'center' }}><div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: getSemaforoColor(efecLin), display: 'inline-block' }}></div></td>
                                                  </tr>
                                                );
                                              })}
                                            </React.Fragment>
                                          );
                                        })}
                                      </React.Fragment>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      {(() => {
                        const efec = totals.cuota_bultos > 0 ? (totals.real_bultos / totals.cuota_bultos) * 100 : 0;
                        return (
                          <tr style={{ position: 'sticky', bottom: 0, backgroundColor: '#1e293b', fontWeight: 'bold', borderTop: '2px solid #475569', zIndex: 2 }}>
                            <td style={{ padding: '6px 4px' }}>TOTALES</td>
                            <td style={{ textAlign: 'right', padding: '6px 4px' }}>{formatNumber(totals.cuota_bultos)}</td>
                            <td style={{ textAlign: 'right', padding: '6px 4px' }}>{formatNumber(totals.real_bultos)}</td>
                            <td style={{ textAlign: 'right', padding: '6px 4px' }}>{efec.toFixed(1)}%</td>
                            <td style={{ textAlign: 'center', padding: '6px 4px' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: getSemaforoColor(efec), display: 'inline-block' }}></div></td>
                          </tr>
                        );
                      })()}
                    </tfoot>
                  </table>
                </div>
              </div>
            ) : (
              /* ══════ ESCRITORIO (estilo DailyPrincipal) ══════ */
              <div className="minimal-table-wrapper">
                <div className="table-header5" style={{ display: 'grid', gridTemplateColumns: GRID }}>
                  <div className="col-name">Especialista / Región / Vendedor / Cliente / SKU</div>
                  <div className="col-data">{comparisonMode === 'FORECAST' ? 'FC BL' : 'Cuota BL'}</div>
                  <div className="col-data">Real BL</div>
                  <div className="col-data">% Efec. BL</div>
                  <div className="col-status">Est. BL</div>
                  <div className="col-var">Var. Anual BL</div>
                </div>

                <div className="table-body5">
                  {treeData.map((esp, eIdx) => {
                    const efecEsp = esp.cuota_bultos > 0 ? (esp.real_bultos / esp.cuota_bultos) * 100 : 0;
                    const isEspOpen = expandedEsp[esp.name];
                    return (
                      <React.Fragment key={`esp-${eIdx}`}>
                        {/* ESPECIALISTA */}
                        <div className="table-row5 advisor-row" style={{ gridTemplateColumns: GRID }} onClick={() => toggleEsp(esp.name)}>
                          <div className="col-name truncate" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            {isEspOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            <span title={esp.name}>{esp.name || '---'}</span>
                          </div>
                          <div className="col-data">{formatNumber(esp.cuota_bultos)}</div>
                          <div className="col-data">{formatNumber(esp.real_bultos)}</div>
                          <div className="col-data bold">{efecEsp.toFixed(1)}%</div>
                          <div className="col-status"><div className="status-dot" style={{ backgroundColor: getSemaforoColor(efecEsp) }}></div></div>
                          <div className="col-var" style={{ fontWeight: 'bold', color: (esp.var_bultos || 0) >= 0 ? '#00ba52' : '#ff4d4d' }}>
                            {esp.var_bultos != null ? `${esp.var_bultos.toFixed(1)}%` : '--'}
                          </div>
                        </div>

                        {/* REGIÓN */}
                        {isEspOpen && esp.regiones.map((reg, rIdx) => {
                          const efecReg = reg.cuota_bultos > 0 ? (reg.real_bultos / reg.cuota_bultos) * 100 : 0;
                          const isRegOpen = expandedReg[`${esp.name}||${reg.name}`];
                          return (
                            <React.Fragment key={`reg-${eIdx}-${rIdx}`}>
                              <div className="table-row5" style={{ backgroundColor: 'rgba(0,0,0,0.08)', gridTemplateColumns: GRID, cursor: 'pointer' }} onClick={() => toggleReg(`${esp.name}||${reg.name}`)}>
                                <div className="col-name truncate" style={{ paddingLeft: '20px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                  {isRegOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                  <span title={reg.name}>{reg.name || '---'}</span>
                                </div>
                                <div className="col-data">{formatNumber(reg.cuota_bultos)}</div>
                                <div className="col-data">{formatNumber(reg.real_bultos)}</div>
                                <div className="col-data bold">{efecReg.toFixed(1)}%</div>
                                <div className="col-status"><div className="status-dot" style={{ backgroundColor: getSemaforoColor(efecReg) }}></div></div>
                                <div className="col-var" style={{ fontWeight: 'bold', color: (reg.var_bultos || 0) >= 0 ? '#00ba52' : '#ff4d4d' }}>
                                  {reg.var_bultos != null ? `${reg.var_bultos.toFixed(1)}%` : '--'}
                                </div>
                              </div>

                              {/* VENDEDOR */}
                              {isRegOpen && (reg.vendedores || []).map((vend, vIdx) => {
                                const efecVend = vend.cuota_bultos > 0 ? (vend.real_bultos / vend.cuota_bultos) * 100 : 0;
                                const isVendOpen = expandedVend[`${esp.name}||${reg.name}||${vend.name}`];
                                return (
                                  <React.Fragment key={`vend-${eIdx}-${rIdx}-${vIdx}`}>
                                    <div className="table-row5" style={{ backgroundColor: 'rgba(0,0,0,0.12)', gridTemplateColumns: GRID, cursor: 'pointer' }} onClick={() => toggleVend(`${esp.name}||${reg.name}||${vend.name}`)}>
                                      <div className="col-name truncate" style={{ paddingLeft: '40px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        {isVendOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                        <span title={vend.name}>{vend.name || '---'}</span>
                                      </div>
                                      <div className="col-data">{formatNumber(vend.cuota_bultos)}</div>
                                      <div className="col-data">{formatNumber(vend.real_bultos)}</div>
                                      <div className="col-data bold">{efecVend.toFixed(1)}%</div>
                                      <div className="col-status"><div className="status-dot" style={{ backgroundColor: getSemaforoColor(efecVend) }}></div></div>
                                      <div className="col-var" style={{ fontWeight: 'bold', color: (vend.var_bultos || 0) >= 0 ? '#00ba52' : '#ff4d4d' }}>
                                        {vend.var_bultos != null ? `${vend.var_bultos.toFixed(1)}%` : '--'}
                                      </div>
                                    </div>

                                    {/* CLIENTE */}
                                    {isVendOpen && (vend.clientes || []).map((cli, cIdx) => {
                                      const efecCli = cli.cuota_bultos > 0 ? (cli.real_bultos / cli.cuota_bultos) * 100 : 0;
                                      const isCliOpen = expandedCli[`${esp.name}||${reg.name}||${vend.name}||${cli.name}`];
                                      return (
                                        <React.Fragment key={`cli-${eIdx}-${rIdx}-${vIdx}-${cIdx}`}>
                                          <div className="table-row5" style={{ backgroundColor: 'rgba(0,0,0,0.16)', gridTemplateColumns: GRID, cursor: 'pointer' }} onClick={() => toggleCli(`${esp.name}||${reg.name}||${vend.name}||${cli.name}`)}>
                                            <div className="col-name truncate" style={{ paddingLeft: '60px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                              {isCliOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                              <span title={cli.name}>{cli.name || '---'}</span>
                                            </div>
                                            <div className="col-data">{formatNumber(cli.cuota_bultos)}</div>
                                            <div className="col-data">{formatNumber(cli.real_bultos)}</div>
                                            <div className="col-data bold">{efecCli.toFixed(1)}%</div>
                                            <div className="col-status"><div className="status-dot" style={{ backgroundColor: getSemaforoColor(efecCli) }}></div></div>
                                            <div className="col-var" style={{ fontWeight: 'bold', color: (cli.var_bultos || 0) >= 0 ? '#00ba52' : '#ff4d4d' }}>
                                              {cli.var_bultos != null ? `${cli.var_bultos.toFixed(1)}%` : '--'}
                                            </div>
                                          </div>

                                          {/* SKU */}
                                          {isCliOpen && cli.lineas.map((lin, lIdx) => {
                                            const efecLin = lin.cuota_bultos > 0 ? (lin.real_bultos / lin.cuota_bultos) * 100 : 0;
                                            return (
                                              <div className="table-row5" key={`lin-${eIdx}-${rIdx}-${vIdx}-${cIdx}-${lIdx}`} style={{ backgroundColor: 'rgba(0,0,0,0.2)', fontSize: '0.7rem', gridTemplateColumns: GRID }}>
                                                <div className="col-name truncate" style={{ paddingLeft: '80px' }} title={lin.name}>{lin.name || '---'}</div>
                                                <div className="col-data">{formatNumber(lin.cuota_bultos)}</div>
                                                <div className="col-data">{formatNumber(lin.real_bultos)}</div>
                                                <div className="col-data">{efecLin.toFixed(1)}%</div>
                                                <div className="col-status"><div className="status-dot" style={{ width: '6px', height: '6px', backgroundColor: getSemaforoColor(efecLin) }}></div></div>
                                                <div className="col-var" style={{ fontWeight: 'bold', color: (lin.var_bultos || 0) >= 0 ? '#00ba52' : '#ff4d4d' }}>
                                                  {lin.var_bultos != null ? `${lin.var_bultos.toFixed(1)}%` : '--'}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </React.Fragment>
                                      );
                                    })}
                                  </React.Fragment>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </div>

                <div className="table-footer5" style={{ display: 'grid', gridTemplateColumns: GRID }}>
                  <div className="col-name">TOTALES</div>
                  {(() => {
                    const efec = totals.cuota_bultos > 0 ? (totals.real_bultos / totals.cuota_bultos) * 100 : 0;
                    const varTotal = (totals.real_bultos_prev > 0)
                      ? ((totals.real_bultos - totals.real_bultos_prev) / totals.real_bultos_prev) * 100
                      : (totals.real_bultos > 0 ? 100 : 0);
                    return (
                      <>
                        <div className="col-data">{formatNumber(totals.cuota_bultos)}</div>
                        <div className="col-data">{formatNumber(totals.real_bultos)}</div>
                        <div className="col-data bold">{efec.toFixed(1)}%</div>
                        <div className="col-status"><div className="status-dot" style={{ backgroundColor: getSemaforoColor(efec) }}></div></div>
                        <div className="col-var" style={{ fontWeight: 'bold', color: varTotal >= 0 ? '#00ba52' : '#ff4d4d' }}>
                          {varTotal.toFixed(1)}%
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>

        <footer className="status-footer">
          {querying ? "SINCRONIZANDO..." : `ESPECIALISTA · BULTOS · ${selectedMeses.join(', ') || 'TODOS'} ${selectedAnios.join(', ') || ''}`}
        </footer>
      </motion.div>
    </motion.div>
  );
};

export default KPIFact;